import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { buildTagColorByName } from '@/features/shared/tags';
import { gridPaths, useTagOptions } from '@/shared/hooks';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { uploadsGridConfig } from '../grid/gridConfig';
import { buildUploadsColumns } from './uploadsColumns';
import { useUploadsTableRowActions } from './useUploadsTableRowActions';
import type { DetailEditEntityDialogProps } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import type { UploadsTableProps } from '@/features/uploads/components/UploadsTable.types';
import type { GridColDef } from '@/ui/primitives';
import type { UploadDto } from '@flowqa/shared';

type UploadsTableControllerInput = Pick<
  UploadsTableProps,
  | 'disablePageSizeSelect'
  | 'fixedFilters'
  | 'initialPageSize'
  | 'initialSortModel'
  | 'noRowsText'
  | 'onRowsChange'
  | 'pageSizeOptions'
  | 'persistKey'
  | 'queryFn'
  | 'queryKey'
  | 'showLastUpdated'
  | 'syncStateToUrl'
  | 'tagOptionsOverride'
>;

type UploadEditDialogSaveHandler = NonNullable<DetailEditEntityDialogProps<string>['onSave']>;

type UploadsTableController = {
  columns: Array<GridColDef<UploadDto>>;
  deleteDialogPurpose: UploadDto['purpose'] | null | undefined;
  deleteLoading: boolean;
  deleteTarget: ReturnType<typeof useUploadsTableRowActions>['deleteTarget'];
  editTarget: UploadDto | null;
  editTargetName: string;
  externalLoading: boolean;
  gridConfig: ReturnType<typeof uploadsGridConfig>;
  gridProps: ReturnType<typeof buildTableGridProps<UploadDto>>;
  handleCloseEditDialog: () => void;
  handleSaveEditDialog: UploadEditDialogSaveHandler;
  isAuthenticated: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  projectId: string | null;
  queryPath: typeof gridPaths.uploads;
  refetchIntervalMs: number | false;
  selectedOrganisationId: string | null;
  togglableColumnFields: string[];
  updateRowTagsColorPendingId: string | null;
};

export const useUploadsTableController = ({
  disablePageSizeSelect,
  fixedFilters,
  initialPageSize,
  initialSortModel,
  noRowsText,
  onRowsChange,
  pageSizeOptions,
  persistKey,
  queryFn,
  queryKey,
  showLastUpdated = false,
  syncStateToUrl = false,
  tagOptionsOverride,
}: UploadsTableControllerInput): UploadsTableController => {
  const { session, loading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [editTarget, setEditTarget] = useState<UploadDto | null>(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const {
    deleteLoading,
    deleteTarget,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onRestoreRow,
    onUpdateRowTagsColor,
    restoreRowPendingId,
    updateRowTagsColorPendingId,
  } = useUploadsTableRowActions({ selectedProjectId });
  const handleRowsChange = useCallback(
    (rows: UploadDto[]) => {
      const hasPending = rows.some((row) => row.status === 'queued' || row.status === 'processing');
      setShouldPoll(hasPending);
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildUploadsColumns({
        editRowPendingId: updateRowTagsColorPendingId,
        deleteRowPendingId,
        onEditRow: setEditTarget,
        onDeleteRow,
        onRestoreRow,
        restoreRowPendingId,
        tagColorByName,
        updateRowTagsColorPendingId,
      }),
    [
      deleteRowPendingId,
      onDeleteRow,
      onRestoreRow,
      restoreRowPendingId,
      tagColorByName,
      updateRowTagsColorPendingId,
    ],
  );
  const togglableColumnFields = useMemo(
    () => columns.map((column) => column.field).filter((field) => field !== 'id'),
    [columns],
  );
  const gridConfig = uploadsGridConfig({
    organisationId: selectedOrganisationId,
    persistKey: persistKey === undefined ? 'uploads-table' : persistKey,
    syncStateToUrl,
    fixedFilters,
    initialSortModel,
  });
  const gridProps = buildTableGridProps<UploadDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions: pageSizeOptions ?? [5, 10, 25, 50],
    initialPageSize,
    disablePageSizeSelect,
    noRowsText,
    showLastUpdated,
    onRowsChange: handleRowsChange,
  });
  const handleCloseEditDialog = useCallback(() => {
    if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
    setEditTarget(null);
  }, [editTarget?.id, updateRowTagsColorPendingId]);
  const handleSaveEditDialog = useCallback<UploadEditDialogSaveHandler>(
    ({ tags, colors }) => {
      if (!editTarget) return;
      onUpdateRowTagsColor({ row: editTarget, tags, colors });
      setEditTarget(null);
    },
    [editTarget, onUpdateRowTagsColor],
  );

  return {
    columns,
    deleteDialogPurpose: deleteTarget?.purpose,
    deleteLoading,
    deleteTarget,
    editTarget,
    editTargetName: editTarget?.fileName ?? '',
    externalLoading: loading,
    gridConfig,
    gridProps,
    handleCloseEditDialog,
    handleSaveEditDialog,
    isAuthenticated: session !== null,
    onDeleteClose,
    onDeleteConfirm,
    projectId: selectedProjectId,
    queryPath: gridPaths.uploads,
    refetchIntervalMs: shouldPoll ? 5000 : false,
    selectedOrganisationId,
    togglableColumnFields,
    updateRowTagsColorPendingId,
  };
};
